import { useEffect, useId, useRef, useState } from 'react';
import { Check, Moon, Sparkles, Sun, X } from 'lucide-react';
import { useTheme } from '../../theme';
import { Button } from '../../components/ui';

export const DASHBOARD_PALETTES = [
  { id: 'cobalt', label: 'Синий', color: '#1769e0', ink: '#ffffff' },
  { id: 'lagoon', label: 'Бирюзовый', color: '#008272', ink: '#ffffff' },
  { id: 'coral', label: 'Коралловый', color: '#cf4051', ink: '#ffffff' },
  { id: 'amber', label: 'Янтарный', color: '#edaa28', ink: '#342407' },
  { id: 'emerald', label: 'Изумрудный', color: '#147d4e', ink: '#ffffff' },
  { id: 'ocean', label: 'Океан', color: '#087bba', ink: '#ffffff' },
  { id: 'tangerine', label: 'Мандариновый', color: '#bf4c12', ink: '#ffffff' },
  { id: 'raspberry', label: 'Малиновый', color: '#c33270', ink: '#ffffff' },
] as const;
type PaletteId = typeof DASHBOARD_PALETTES[number]['id'];
const STORAGE_KEY = 'intez-dashboard-palette-v1';

function initialPalette(): PaletteId {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return DASHBOARD_PALETTES.find(p => p.id === value)?.id ?? 'cobalt';
  } catch { return 'cobalt'; }
}

export function DashboardAppearance() {
  const { theme, setTheme } = useTheme();
  const [palette, setPalette] = useState(initialPalette);
  const [persisted, setPersisted] = useState(true);
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => {
    document.documentElement.dataset.dashboardPalette = palette;
    try { window.localStorage.setItem(STORAGE_KEY, palette); setPersisted(true); }
    catch { setPersisted(false); }
    return () => { delete document.documentElement.dataset.dashboardPalette; };
  }, [palette]);

  useEffect(() => {
    if (!open) return;
    close.current?.focus({ preventScroll: true });
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !host.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus({ preventScroll: true });
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return <div className="dashboard-appearance" ref={host}>
    <Button ref={trigger} aria-label="Оформление" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(value => !value)}><Sparkles /> Оформление</Button>
    {open && <section className="dashboard-appearance__panel" id={id} role="dialog" aria-modal="false" aria-labelledby={`${id}-title`}>
      <header><div><small>ВАША ГЛАВНАЯ</small><h2 id={`${id}-title`}>Цвет и настроение</h2></div><button ref={close} className="dashboard-appearance__close" type="button" aria-label="Закрыть оформление" onClick={() => { setOpen(false); trigger.current?.focus(); }}><X /></button></header>
      <div className="dashboard-appearance__themes" aria-label="Тема интерфейса">
        <button type="button" aria-pressed={theme === 'light'} onClick={() => setTheme('light')}><Sun /> Светлая</button>
        <button type="button" aria-pressed={theme === 'dark'} onClick={() => setTheme('dark')}><Moon /> Тёмная</button>
      </div>
      <p>Насыщенный акцент, мягкий рельеф. Графики сохраняют собственные цвета.</p>
      <div className="dashboard-appearance__palettes" role="group" aria-label="Цвет главной">
        {DASHBOARD_PALETTES.map(item => <button type="button" key={item.id} data-palette-option={item.id} aria-label={item.label} aria-pressed={palette === item.id} onClick={() => setPalette(item.id)}>
          <span style={{ backgroundColor: item.color, color: item.ink }}>{palette === item.id && <Check />}</span><small>{item.label}</small>
        </button>)}
      </div>
      <footer role="status">{persisted ? 'Сохранено на этом устройстве' : 'Сохранение недоступно · только на этой странице'}</footer>
    </section>}
  </div>;
}
